import type { StoragePlugin } from "@capacitor/core";
import { differenceInMonths } from "date-fns";
import { analytics } from "lib/analytics";
import { Sentry } from "lib/sentry";
import { schema } from "shared/analytics";

export interface ReviewContext {
  requestReview: () => void;
  userDemonstratedEngagement: () => void;
}

export const nullAppStoreReviewContext: ReviewContext = {
  requestReview: () => undefined,
  userDemonstratedEngagement: () => undefined,
};

const hasBeenRequestedRecently = async (
  keyValueStore: Pick<StoragePlugin, "get">
): Promise<boolean> => {
  try {
    const lastReviewRequest = await keyValueStore.get({
      key: "lastReviewRequest",
    });
    const MONTHS_ELAPSED = 4;

    return (
      lastReviewRequest &&
      differenceInMonths(new Date(), new Date(lastReviewRequest.value)) <
        MONTHS_ELAPSED
    );
  } catch (err) {
    Sentry.captureException(err);
    return true;
  }
};

export const requestAppStoreReview = async ({
  hasUserDemonstratedEngagement,
  isHighReviewer,
  keyValueStore,
  rateRequest,
}: {
  hasUserDemonstratedEngagement: boolean;
  isHighReviewer: boolean;
  keyValueStore: Pick<StoragePlugin, "get" | "set">;
  rateRequest: () => Promise<void>;
}): Promise<void> => {
  if (
    !isHighReviewer ||
    (await hasBeenRequestedRecently(keyValueStore)) ||
    !hasUserDemonstratedEngagement
  ) {
    return;
  }

  try {
    await rateRequest();
    await keyValueStore.set({
      key: "lastReviewRequest",
      value: new Date().toString(),
    });
    analytics.trackEvent(schema.APP_STORE_REVIEW_REQUESTED);
  } catch (err) {
    Sentry.captureException(err);
  }
};
